/**
 * useScreenAnnouncements Hook
 * Ekran için aktif duyuruları yönetir (header marquee'sinde gösterilir).
 * - GET /api/QuickSaleAnnouncement/for-screen/{screenId}?branchId=...
 * - Realtime: SignalR `OnAnnouncementChanged` event'inde refetch
 * - Smart-expire timer: en yakın endAt'ta anlık expire + refetch
 *
 * Not: Kiosk ürün görünürlük SignalR bağlantısı (kioskSignalRService.connect)
 * tarihsel olarak bu effect içinde kurulduğu için davranış korunarak burada bırakıldı.
 */

import { useState, useEffect, useRef } from 'react';
import { announcementApi, QuickSaleAnnouncementDto } from '../../../../services/announcementApiService';
import announcementSignalRService from '../../../../services/announcementSignalRService';
import kioskSignalRService from '../../../../services/kioskSignalRService';

export interface UseScreenAnnouncementsParams {
  /** Şirket ID */
  companyId: string | null;
  /** Aktif ekran ID (currentScreen?.id) */
  screenId: string | undefined;
  /** Ekranın şube ID'si (currentScreen?.branchId) */
  screenBranchId: string | null | undefined;
  /** Kullanıcının şube ID'si (quickSaleUserData?.branchesId) */
  userBranchesId: string | null | undefined;
}

export interface UseScreenAnnouncementsReturn {
  activeAnnouncements: QuickSaleAnnouncementDto[];
}

/**
 * Ekran duyuru sistemi hook'u
 */
export const useScreenAnnouncements = ({
  companyId,
  screenId,
  screenBranchId,
  userBranchesId,
}: UseScreenAnnouncementsParams): UseScreenAnnouncementsReturn => {
  const [activeAnnouncements, setActiveAnnouncements] = useState<QuickSaleAnnouncementDto[]>([]);

  // Aktif fetch fonksiyonunu ref ile sakla → hem ana effect hem de smart-expire effect
  // aynı fonksiyonu çağırabilsin. Ref'in stale olmaması için her render'da güncellenir.
  const fetchAnnouncementsRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (!companyId || !screenId) {
      console.log('📢 [QuickSaleScreen Announcements] ⏸️ Bekleniyor:', { companyId: !!companyId, currentScreenId: screenId });
      fetchAnnouncementsRef.current = null;
      return;
    }

    let cancelled = false;
    const numericScreenId = parseInt(String(screenId), 10);
    if (isNaN(numericScreenId)) {
      console.warn('📢 [QuickSaleScreen Announcements] ⚠️ ScreenId numeric değil, fetch iptal:', screenId);
      return;
    }
    const branchIdForFetch = screenBranchId || userBranchesId || null;
    console.log('📢 [QuickSaleScreen Announcements] 🚀 Fetch başlıyor:', {
      companyId,
      screenId: numericScreenId,
      branchId: branchIdForFetch,
    });

    const fetchAnnouncements = async () => {
      try {
        const list = await announcementApi.getActiveForScreen(numericScreenId, branchIdForFetch);
        console.log('📢 [QuickSaleScreen Announcements] ✅ Response:', {
          count: list?.length ?? 0,
          items: list,
        });
        if (!cancelled) setActiveAnnouncements(list);
      } catch (err) {
        console.error('📢 [QuickSaleScreen Announcements] ❌ Fetch hatası:', err);
      }
    };
    fetchAnnouncementsRef.current = fetchAnnouncements;

    fetchAnnouncements();

    // Periyodik refresh (60 sn) — DailyStartTime/EndTime, weekday, monthly geçişleri için.
    // EndAt anlık expire'ı için ayrıca aşağıdaki "smart expire" effect'i çalışıyor.
    const interval = window.setInterval(fetchAnnouncements, 60 * 1000);

    // SignalR canlı tetikleme
    const cbId = `quicksale_screen_${numericScreenId}`;
    console.log('📢 [QuickSaleScreen Announcements] 🔌 SignalR connect çağrılıyor:', { companyId, cbId });
    announcementSignalRService
      .connect(companyId)
      .then(() => {
        console.log('📢 [QuickSaleScreen Announcements] 🔌 SignalR connect tamamlandı. State:', announcementSignalRService.getConnectionState());
      })
      .catch((err) => {
        console.error('📢 [QuickSaleScreen Announcements] 🔌 SignalR connect hatası:', err);
      });
    announcementSignalRService.onAnnouncementChanged(cbId, (payload) => {
      console.log('📢 [QuickSaleScreen Announcements] 🔔 SignalR event alındı, refetching:', payload);
      fetchAnnouncements();
    });

    // 🖥️ Plan 2 — Kiosk Urun Gorunurluk SignalR baglantisi (paralel ekleme).
    // KioskProductsModal henuz acilmamis olsa bile burada baglanmak, modal acildiginda
    // anlik state'i hazir tutar; ek olarak diger admin sekmelerinde modal acik ise
    // cross-tab senkronizasyon hemen calisir.
    kioskSignalRService
      .connect(companyId)
      .catch((err) => {
        console.error('🖥️ [QuickSaleScreen KioskVis] 🔌 SignalR connect hatası:', err);
      });

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      announcementSignalRService.removeAnnouncementChangedCallback(cbId);
      fetchAnnouncementsRef.current = null;
    };
  }, [companyId, screenId, screenBranchId, userBranchesId]);

  // ⏰ Smart-expire timer: aktif duyuruların EN YAKIN endAt'ına bir setTimeout kur.
  // O zaman geldiğinde anında refetch → backend artık expire olanı döndürmez → marquee
  // saniye hassasiyetiyle yenilenir. Aynı zamanda lokal optimistic filtering yapıyoruz
  // (fetch dönmeden önce de listeden düşürmek için).
  useEffect(() => {
    if (activeAnnouncements.length === 0) return;
    const now = Date.now();

    const futureEnds = activeAnnouncements
      .map((a) => new Date(a.endAt).getTime())
      .filter((t) => Number.isFinite(t) && t > now)
      .sort((a, b) => a - b);

    if (futureEnds.length === 0) return;

    const nextEnd = futureEnds[0];
    // +1sn tolerans (server saatiyle hafif fark olabilir, garanti expire için)
    const delay = Math.max(1000, nextEnd - now + 1000);

    console.log(
      `📢 [QuickSaleScreen Announcements] ⏰ Sonraki expire: ${new Date(nextEnd).toLocaleString()} (${(delay / 1000).toFixed(0)}sn sonra)`
    );

    const timer = window.setTimeout(() => {
      console.log('📢 [QuickSaleScreen Announcements] ⏰ EXPIRE timer tetiklendi, lokal filtre + refetch.');
      // Lokal optimistic: backend cevabı gelmeden önce bile UI'dan düş.
      const cutoff = Date.now();
      setActiveAnnouncements((prev) =>
        prev.filter((a) => {
          const t = new Date(a.endAt).getTime();
          return Number.isFinite(t) && t > cutoff;
        })
      );
      // Sonra backend ile senkronize et (tutarlı liste için).
      fetchAnnouncementsRef.current?.();
    }, delay);

    return () => window.clearTimeout(timer);
  }, [activeAnnouncements]);

  return { activeAnnouncements };
};

export default useScreenAnnouncements;
